import { ColorMode, ColorModePreference } from '@color';
import { localStore } from './LocalStore.svelte';

export class Preferences {
  trackpadScrollSpeed = $state(1);
  panInertialScrollSensitivity = $state(1);

  #bottomBarVisible = localStore('flBottomBarVisible', false);
  get bottomBarVisible() {
    return this.#bottomBarVisible.value;
  }
  set bottomBarVisible(value: boolean) {
    this.#bottomBarVisible.value = value;
  }

  #topBarVisible = localStore('flTopBarVisible', true);
  get topBarVisible() {
    return this.#topBarVisible.value;
  }
  set topBarVisible(value: boolean) {
    this.#topBarVisible.value = value;
  }

  #leftBarVisible = localStore('flLeftBarVisible', true);
  get leftBarVisible() {
    return this.#leftBarVisible.value;
  }
  set leftBarVisible(value: boolean) {
    this.#leftBarVisible.value = value;
  }

  #rightBarVisible = localStore('flRightBarVisible', false);
  get rightBarVisible() {
    return this.#rightBarVisible.value;
  }
  set rightBarVisible(value: boolean) {
    this.#rightBarVisible.value = value;
  }

  #backupDoc = localStore('flBackupDoc', '');

  get backupDoc(): string {
    return this.#backupDoc.value;
  }

  set backupDoc(value: string) {
    this.#backupDoc.value = value;
  }

  #screencastMode = localStore('flScreencastMode', false);

  get screencastMode() {
    return this.#screencastMode.value;
  }

  set screencastMode(value: boolean) {
    this.#screencastMode.value = value;
  }

  // The system-wide color mode.
  systemColorMode = $state(ColorMode.LIGHT);

  // The user's preferred color mode for the app.
  #colorModePreference = localStore('flColorMode', ColorModePreference.SYSTEM);
  get colorModePreference() {
    return this.#colorModePreference.value;
  }
  set colorModePreference(value: ColorModePreference) {
    this.#colorModePreference.value = value;
  }

  // Time (seconds since epoch) when the demo password access expires.
  // Undefined or 0 means no valid password session.
  #demoPasswordExpiry = localStore<number>('flDemoPasswordExpiry', 0);
  get demoPasswordExpiry() {
    return this.#demoPasswordExpiry.value;
  }
  set demoPasswordExpiry(value: number) {
    this.#demoPasswordExpiry.value = value;
  }

  // The effective color mode.
  colorMode = $derived.by(() => {
    switch (this.colorModePreference) {
      case ColorModePreference.LIGHT:
        return ColorMode.LIGHT;
      case ColorModePreference.DARK:
        return ColorMode.DARK;
      case ColorModePreference.SYSTEM:
        return this.systemColorMode;
    }
  });

  private systemPrefersDarkQuery: MediaQueryList | undefined;

  constructor() {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.systemPrefersDarkQuery = window.matchMedia('(prefers-color-scheme: dark)');
      // React to system colour-scheme changes in real browsers
      this.systemPrefersDarkQuery.addEventListener('change', () => {
        this.updateColorMode();
      });
    }
    // Initialise effective colour mode (defaults to LIGHT in non-browser env)
    this.updateColorMode();
  }

  private updateColorMode() {
    const prefersDark = this.systemPrefersDarkQuery?.matches ?? false;
    this.systemColorMode = prefersDark ? ColorMode.DARK : ColorMode.LIGHT;
  }

  toggleColorMode() {
    if (this.colorModePreference === ColorModePreference.SYSTEM) {
      this.colorModePreference =
        this.systemColorMode == ColorMode.LIGHT
          ? ColorModePreference.DARK
          : ColorModePreference.LIGHT;
    } else {
      this.colorModePreference =
        this.colorModePreference === ColorModePreference.LIGHT
          ? ColorModePreference.DARK
          : ColorModePreference.LIGHT;
    }
  }
}

export const prefs = new Preferences();
